/**
 * ──────────────────────────────────────────────────────────────────────────────
 *  appOnHover directive that exposes the "hover" state of an element
 * ──────────────────────────────────────────────────────────────────────────────
 *
 * How it works
 * ------------
 * • Listens to **mouseenter** / **mouseleave** on the host element.
 * • Internally stores the state in a signal.
 * • Exposes the state as a readonly signal.
 *
 * Usage
 * -------------
 * ```html
 * <tr appOnHover #your_row_variable_name="onHover">
 *   <app-all-icons *ngIf="your_row_variable_name.hovered()" [iconName]="edit.svg"></app-all-icons>
 * </tr>
 * ```
 */
import { Directive, ElementRef, inject, NgZone, OnDestroy, signal } from '@angular/core';

@Directive({
  selector: '[appOnHover]',
  standalone: true,
  exportAs: 'onHover',
})
export class OnHoverDirective implements OnDestroy {
  private elementRef = inject(ElementRef<HTMLElement>) as ElementRef<HTMLElement>;
  private ngZone = inject(NgZone);

  private readonly _hovered = signal<boolean>(false);
  readonly hovered = this._hovered.asReadonly();

  constructor(
  ) {
    this.ngZone.runOutsideAngular(() => {
      this.elementRef.nativeElement.addEventListener('mouseenter', this.onEnter);
      this.elementRef.nativeElement.addEventListener('mouseleave', this.onLeave);
    });
  }

  ngOnDestroy(): void {
    this.elementRef.nativeElement.removeEventListener('mouseenter', this.onEnter);
    this.elementRef.nativeElement.removeEventListener('mouseleave', this.onLeave);
  }

  private readonly onEnter = (): void => {
    this._hovered.set(true);
  };

  private readonly onLeave = (): void => {
    this._hovered.set(false);
  };
}
